import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX } from 'lucide-react';
import { useSpotify } from '../context/SpotifyContext';
import { apiCall } from '../services/api';

const NowPlayingModal = () => {
  const {
    currentTrack,
    isPlaying,
    setIsPlaying,
    accessToken,
    showNowPlaying,
    setShowNowPlaying,
    progressMs,
    setProgressMs,
    durationMs,
    volume,
  } = useSpotify();

  const [lyrics, setLyrics] = useState([]);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [activeLine, setActiveLine] = useState(0);
  const [lyricsSync, setLyricsSync] = useState(false); // Track if lyrics are synced
  const lyricsRef = useRef(null);
  const progressInterval = useRef(null);
  const hasInitialized = useRef(false);

  // Ensure modal is closed on initial mount - prevent auto-opening
  useEffect(() => {
    if (!hasInitialized.current) {
      setShowNowPlaying(false);
      hasInitialized.current = true;
    }
  }, [setShowNowPlaying]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showNowPlaying) {
        setShowNowPlaying(false);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showNowPlaying, setShowNowPlaying]);

  // Fetch lyrics when track changes
  useEffect(() => {
    if (!currentTrack || !showNowPlaying) {
      console.log('❌ Lyrics fetch skipped:', { hasTrack: !!currentTrack, modalOpen: showNowPlaying });
      return;
    }
    
    const fetchLyrics = async () => {
      setLyricsLoading(true);
      setLyrics([]);
      setActiveLine(0);
      setLyricsSync(false);
      
      try {
        // Handle both string and array formats for artists
        let artistName = '';
        if (typeof currentTrack.artists === 'string') {
          artistName = currentTrack.artists.split(',')[0]?.trim() || '';
        } else if (Array.isArray(currentTrack.artists)) {
          artistName = currentTrack.artists[0]?.name || currentTrack.artists[0] || '';
        }
        
        if (!artistName || !currentTrack.name) {
          console.error('❌ Missing track or artist name:', { track: currentTrack.name, artist: artistName });
          setLyricsLoading(false);
          return;
        }
        
        const artist = encodeURIComponent(artistName);
        const track = encodeURIComponent(currentTrack.name || '');
        
        const lyricsUrl = `/spotify/lyrics?track=${track}&artist=${artist}`;
        console.log('🎵 Fetching lyrics:', { track: currentTrack.name, artist: artistName, url: lyricsUrl });
        
        const data = await apiCall(lyricsUrl);
        console.log('🎵 Lyrics response:', data);
        
        if (data.lyrics && Array.isArray(data.lyrics) && data.lyrics.length > 0) {
          console.log(`✅ Loaded ${data.lyrics.length} lines of lyrics from ${data.source}`);
          console.log(`🎯 Lyrics synced: ${data.synced ? 'YES (timestamped)' : 'NO (estimated)'}`);
          setLyrics(data.lyrics);
          setLyricsSync(data.synced || false);
        } else {
          console.warn('⚠️ No lyrics found:', data);
        }
      } catch (err) {
        console.error('❌ Failed to fetch lyrics:', err);
      } finally {
        setLyricsLoading(false);
      }
    };
    
    fetchLyrics();
  }, [currentTrack?.name, showNowPlaying]);

  // Update active line based on progress (for synced lyrics use timestamps, for unsynced use estimation)
  useEffect(() => {
    if (!lyrics.length || !isPlaying) return;
    
    if (lyricsSync) {
      // Use actual timestamps for synced lyrics
      let newActiveLine = 0;
      for (let i = 0; i < lyrics.length; i++) {
        if (lyrics[i].time && progressMs >= lyrics[i].time) {
          newActiveLine = i;
        } else {
          break;
        }
      }
      setActiveLine(newActiveLine);
    } else {
      // Fallback to estimation for unsynced lyrics
      if (durationMs) {
        const msPerLine = durationMs / lyrics.length;
        const currentLine = Math.min(Math.floor(progressMs / msPerLine), lyrics.length - 1);
        setActiveLine(currentLine);
      }
    }
  }, [progressMs, lyrics, lyricsSync, durationMs, isPlaying]);

  // Auto-scroll lyrics to active line
  useEffect(() => {
    if (lyricsRef.current && lyrics.length > 0) {
      const activeEl = lyricsRef.current.querySelector(`[data-line="${activeLine}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeLine, lyrics.length]);

  // Increment progress locally
  useEffect(() => {
    if (isPlaying && showNowPlaying) {
      progressInterval.current = setInterval(() => {
        setProgressMs(prev => Math.min(prev + 1000, durationMs));
      }, 1000);
    }
    return () => clearInterval(progressInterval.current);
  }, [isPlaying, showNowPlaying, durationMs, setProgressMs]);

  const togglePlayback = useCallback(async () => {
    if (!accessToken) return;
    try {
      if (isPlaying) {
        await apiCall(`/spotify/playback/pause?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
        setIsPlaying(false);
      } else {
        await apiCall(`/spotify/playback/play?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Toggle playback error:', error);
    }
  }, [accessToken, isPlaying, setIsPlaying]);

  const skipNext = useCallback(async () => {
    if (!accessToken) return;
    try {
      await apiCall(`/spotify/playback/next?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
    } catch (error) {
      console.error('Skip next error:', error);
    }
  }, [accessToken]);

  const skipPrevious = useCallback(async () => {
    if (!accessToken) return;
    try {
      await apiCall(`/spotify/playback/previous?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
    } catch (error) {
      console.error('Skip previous error:', error);
    }
  }, [accessToken]);

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!showNowPlaying || !currentTrack) return null;

  const progressPct = durationMs > 0 ? (progressMs / durationMs) * 100 : 0;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={() => setShowNowPlaying(false)}
    >
      {/* Close Button - Outside the modal for better visibility */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowNowPlaying(false);
        }}
        className="absolute top-6 right-6 z-[100000] p-4 bg-red-600 hover:bg-red-500 rounded-full transition-colors shadow-2xl border-2 border-white"
        data-testid="close-now-playing"
      >
        <X size={32} className="text-white" strokeWidth={3} />
      </button>
      
      <div
        className="relative bg-black border border-white/20 rounded-2xl w-full max-w-4xl overflow-hidden hover:border-white/40 hover:shadow-[0_0_40px_rgba(255,255,255,0.08)] transition-all duration-500"
        onClick={(e) => e.stopPropagation()}
        data-testid="now-playing-modal"
      >

        <div className="flex flex-col md:flex-row">
          {/* Left Side: Vinyl + Track Info */}
          <div className="flex-1 flex flex-col items-center justify-center p-8 md:p-12">
            {/* Spinning Vinyl Disc */}
            <div className="relative w-64 h-64 md:w-72 md:h-72 mb-8">
              {/* Outer vinyl disc */}
              <div
                className={`absolute inset-0 rounded-full bg-gradient-to-br from-gray-900 via-gray-800 to-black border-2 border-gray-700 shadow-[0_0_60px_rgba(0,0,0,0.8)] ${
                  isPlaying ? 'animate-spin' : ''
                }`}
                style={{
                  animationDuration: '4s',
                  animationTimingFunction: 'linear',
                  background: `
                    radial-gradient(circle at center, transparent 30%, rgba(40,40,40,0.6) 31%, rgba(30,30,30,0.4) 35%, transparent 36%,
                    transparent 42%, rgba(50,50,50,0.3) 43%, transparent 44%,
                    transparent 52%, rgba(40,40,40,0.4) 53%, transparent 54%,
                    transparent 62%, rgba(50,50,50,0.2) 63%, transparent 64%,
                    transparent 72%, rgba(40,40,40,0.3) 73%, transparent 74%,
                    transparent 82%, rgba(50,50,50,0.2) 83%, transparent 84%,
                    transparent 92%, rgba(40,40,40,0.4) 93%, transparent 94%),
                    linear-gradient(135deg, #1a1a1a, #0a0a0a, #1a1a1a)
                  `
                }}
              >
                {/* Vinyl grooves shine effect */}
                <div className="absolute inset-0 rounded-full opacity-20"
                  style={{
                    background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.05) 10%, transparent 20%, rgba(255,255,255,0.03) 40%, transparent 60%)'
                  }}
                />
                {/* Center label / album art */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-gray-600 shadow-inner">
                  <img
                    src={currentTrack.image}
                    alt={currentTrack.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Center spindle hole */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gray-900 border border-gray-600" />
              </div>
            </div>

            {/* Track Info */}
            <div className="text-center mb-6 w-full max-w-xs">
              <h2 className="text-white text-xl font-bold truncate">{currentTrack.name}</h2>
              <p className="text-gray-400 text-sm mt-1 truncate">{currentTrack.artists}</p>
              {currentTrack.album && (
                <p className="text-gray-500 text-xs mt-1 truncate">{currentTrack.album}</p>
              )}
            </div>

            {/* Progress Bar */}
            <div className="w-full max-w-xs mb-4">
              <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden cursor-pointer group">
                <div
                  className="h-full bg-white rounded-full transition-all duration-1000 group-hover:bg-green-400"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-500 text-xs">{formatTime(progressMs)}</span>
                <span className="text-gray-500 text-xs">{formatTime(durationMs)}</span>
              </div>
            </div>

            {/* Playback Controls */}
            <div className="flex items-center gap-6">
              <button
                onClick={skipPrevious}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
              >
                <SkipBack size={22} />
              </button>
              <button
                onClick={togglePlayback}
                className="p-4 bg-white hover:bg-gray-200 rounded-full transition-all hover:scale-105 shadow-lg text-black"
                data-testid="now-playing-play-btn"
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
              </button>
              <button
                onClick={skipNext}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
              >
                <SkipForward size={22} />
              </button>
            </div>
          </div>

          {/* Right Side: Lyrics */}
          <div className="flex-1 border-t md:border-t-0 md:border-l border-white/10 flex flex-col max-h-[70vh] md:max-h-[600px]">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-white font-semibold text-sm tracking-wider uppercase">Lyrics</h3>
            </div>
            <div ref={lyricsRef} className="flex-1 overflow-y-auto px-6 py-4 scroll-smooth">
              {lyricsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              ) : lyrics.length > 0 ? (
                <div className="space-y-3 pb-20">
                  {lyrics.map((line, idx) => (
                    <p
                      key={idx}
                      data-line={idx}
                      className={`transition-all duration-500 leading-relaxed ${
                        idx === activeLine
                          ? 'text-white text-lg font-semibold scale-[1.02] origin-left'
                          : idx < activeLine
                          ? 'text-gray-600 text-base'
                          : 'text-gray-500 text-base'
                      }`}
                    >
                      {typeof line === 'object' ? line.text : line}
                    </p>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <Volume2 size={28} className="text-gray-600" />
                  </div>
                  <p className="text-gray-500 text-sm">Lyrics not available</p>
                  <p className="text-gray-600 text-xs mt-1">Enjoy the music</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NowPlayingModal;
